import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertTriangle,
  CreditCard,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import ConfirmSubscriptionAction from '@/components/subscription/ConfirmSubscriptionAction';

const STATUS_META = {
  active: { label: 'Active', className: 'bg-emerald-100 text-emerald-700' },
  past_due: { label: 'Payment past due', className: 'bg-rose-100 text-rose-700' },
  cancelled: { label: 'Cancelled', className: 'bg-slate-200 text-slate-700' },
  // Trial Premium (coupon-granted) has its own distinct status value so it
  // never collides with anything else. The legacy 'beta'/'trial' school-level
  // statuses were removed with the Oct 2026 subscription-model refactor.
  trial_premium: { label: 'Trial Premium', className: 'bg-indigo-100 text-indigo-700' },
};

function formatDate(isoString) {
  if (!isoString) return null;
  try {
    return format(parseISO(isoString), 'MMMM d, yyyy');
  } catch (_e) {
    return null;
  }
}

export default function SubscriptionStatusCard({
  status,
  planName,
  isPaidActive,
  isCancelling,
  isBusy,
  pendingPlanName,
  pendingBillingCycle,
  pendingChangeEffectiveDate,
  onSwitchBillingCycle,
  onCancelSubscription,
  onManagePayment,
  onChoosePlan,
}) {
  const [switchCycleOpen, setSwitchCycleOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const meta = STATUS_META[status.status] || { label: status.status, className: 'bg-slate-100 text-slate-700' };
  const nextBillingDate = formatDate(status.expires);
  const cycleLabel = status.billing_cycle === 'annual' ? 'Annual' : status.billing_cycle === 'monthly' ? 'Monthly' : null;
  const pendingCycleLabel = pendingBillingCycle === 'annual' ? 'Annual' : pendingBillingCycle === 'monthly' ? 'Monthly' : null;
  const pendingEffectiveDate = formatDate(pendingChangeEffectiveDate);
  const hasPendingChange = Boolean((pendingPlanName || pendingBillingCycle) && pendingEffectiveDate);
  const isAnnual = status.billing_cycle === 'annual';
  const isTrialPremium = status.status === 'trial_premium';
  const trialEndsOn = formatDate(status.trial_ends_at);
  const trialDays = status.trial_days_remaining;

  const periodEndPhrase = nextBillingDate
    ? `the end of your current billing period (${nextBillingDate})`
    : 'the end of your current billing period';

  // Run the page-level handler, then close the dialog once it settles. The
  // handlers catch their own errors (toasts / inline banners), so the dialog
  // always closes and feedback surfaces through the page.
  const runAndClose = async (closeFn, handler) => {
    await handler();
    closeFn(false);
  };

  return (
    <Card className="border-slate-200 mb-8">
      <CardContent className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-sm text-slate-500">Current Status</p>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Badge className={meta.className} data-testid="subscription-status-badge">
                {isCancelling ? 'Cancelling at period end' : meta.label}
              </Badge>
              {planName && !isTrialPremium && (
                <span className="text-sm text-slate-700">{planName} Plan</span>
              )}
              {cycleLabel && (
                <span className="text-sm text-slate-500">· {cycleLabel} billing</span>
              )}
            </div>
            {nextBillingDate && (status.status === 'active' || status.status === 'past_due') && (
              <p className="text-sm text-slate-500 mt-2">
                {isCancelling ? 'Access ends' : 'Next billing date'}: <span className="font-medium text-slate-700">{nextBillingDate}</span>
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-500">Subs Connected</p>
            <p className="text-xl font-bold text-slate-900">
              {status.toc_count} / {status.toc_limit === 'unlimited' ? '∞' : status.toc_limit}
            </p>
          </div>
        </div>

        {status.status === 'past_due' && (
          <div className="mt-4 p-3 bg-rose-50 rounded-lg flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 flex-1">
              <AlertTriangle className="h-5 w-5 text-rose-600 flex-shrink-0" />
              <p className="text-sm text-rose-700">
                Your last payment failed. Update your payment method to keep your subscription active.
              </p>
            </div>
            <Button
              size="sm"
              onClick={onManagePayment}
              disabled={isBusy}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="manage-payment-btn"
            >
              {isBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
              Manage payment
            </Button>
          </div>
        )}

        {isTrialPremium && (
          <div className="mt-4 p-3 bg-indigo-50 rounded-lg" data-testid="trial-premium-banner">
            <p className="text-sm font-medium text-indigo-800">
              Trial Premium{trialEndsOn ? ` — full access until ${trialEndsOn}` : ' — full access'}
            </p>
            {typeof trialDays === 'number' && (
              <p className="text-sm text-indigo-700 mt-1" data-testid="trial-days-remaining">
                {trialDays === 1 ? '1 day remaining' : `${trialDays} days remaining`}
              </p>
            )}
            {!hasPendingChange && (
              <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <p className="text-sm text-indigo-700 flex-1">
                  When your trial ends your account moves to the free Head Start plan. Choose a paid
                  plan to keep your full access — it starts the day your trial ends.
                </p>
                <Button
                  size="sm"
                  onClick={onChoosePlan}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="trial-choose-plan-btn"
                >
                  Choose your plan
                </Button>
              </div>
            )}
          </div>
        )}

        {hasPendingChange && (
          <div className="mt-4 p-3 bg-amber-50 rounded-lg" data-testid="pending-change-banner">
            <p className="text-sm text-amber-700">
              {pendingPlanName && (isTrialPremium
                ? `Your ${pendingPlanName} plan starts on ${pendingEffectiveDate}, when your trial ends. Your first period is already paid. `
                : `Your plan is changing to ${pendingPlanName} on ${pendingEffectiveDate}. `)}
              {pendingBillingCycle && `Your billing is switching to ${pendingCycleLabel} on ${pendingEffectiveDate}.`}
            </p>
          </div>
        )}

        {isPaidActive && !isCancelling && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSwitchCycleOpen(true)}
              disabled={isBusy}
              data-testid="switch-billing-cycle-btn"
            >
              {isBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Switch to {isAnnual ? 'monthly' : 'annual'} billing
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCancelOpen(true)}
              disabled={isBusy}
              data-testid="cancel-subscription-btn"
            >
              Cancel subscription
            </Button>
          </div>
        )}

        {isCancelling && (
          <div className="mt-4 p-3 bg-amber-50 rounded-lg" data-testid="cancellation-notice">
            <p className="text-sm text-amber-700">
              Your subscription is cancelling{nextBillingDate ? ` on ${nextBillingDate}` : ''}. You'll keep access until then.
            </p>
            <p className="text-sm text-amber-700 mt-2">
              After that date your account automatically moves to the <strong>free Head Start plan</strong>:
              1 school, 8 Subs, 12 postings/month, no in-app messaging, and basic analytics only.
              Actions above those limits disable themselves — existing data stays visible but new
              actions are blocked.
            </p>
            <p className="text-sm text-amber-700 mt-2">
              Changed your mind, or want to keep some of what you have? Use{' '}
              <strong>Manage payment method</strong> below to resume your subscription in the Stripe
              billing portal — or cancel and pick a lower paid plan instead.
            </p>
          </div>
        )}
      </CardContent>

      {/* Billing-cycle switch confirmation — the payment consequence differs by
          direction: monthly -> annual applies immediately with a prorated
          charge today; annual -> monthly is only scheduled for the period end
          with no new payment. */}
      <ConfirmSubscriptionAction
        open={switchCycleOpen}
        onOpenChange={setSwitchCycleOpen}
        title={isAnnual ? 'Switch to monthly billing?' : 'Switch to annual billing?'}
        summary={
          isAnnual
            ? `No new payment now. The switch is scheduled for ${periodEndPhrase}.`
            : 'Applies immediately, and a new payment is charged today.'
        }
        confirmLabel={isAnnual ? 'Schedule switch to monthly' : 'Switch to annual billing'}
        confirmTestId="confirm-switch-cycle-btn"
        onConfirm={() => runAndClose(setSwitchCycleOpen, () => onSwitchBillingCycle(isAnnual ? 'monthly' : 'annual'))}
        isBusy={isBusy}
      >
        {isAnnual ? (
          <ul className="space-y-1.5 list-disc pl-5">
            <li>Your {planName || 'current'} plan stays on annual billing (with its discount) until {nextBillingDate || 'your renewal date'}.</li>
            <li>From that date you are billed monthly at the monthly rate — nothing is charged today.</li>
            <li>You can change your mind any time before it takes effect.</li>
          </ul>
        ) : (
          <ul className="space-y-1.5 list-disc pl-5">
            <li>You are charged your plan&apos;s annual price today, prorated — unused time on your monthly cycle is credited against the charge.</li>
            <li>Annual billing saves 16-20% versus paying month by month.</li>
            <li>Your subscription then renews automatically every year at the annual rate.</li>
          </ul>
        )}
      </ConfirmSubscriptionAction>

      {/* Cancellation confirmation — no further charges; access until period
          end, then the free Head Start plan. */}
      <ConfirmSubscriptionAction
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancel your subscription?"
        summary={`No further charges. Your ${planName || 'current'} plan stays active until ${periodEndPhrase}, then your account moves to the free Head Start plan.`}
        confirmLabel="Cancel subscription"
        cancelLabel="Keep subscription"
        confirmTestId="confirm-cancel-subscription-btn"
        onConfirm={() => runAndClose(setCancelOpen, onCancelSubscription)}
        isBusy={isBusy}
        destructive
      >
        <ul className="space-y-1.5 list-disc pl-5">
          <li>You keep full access to every feature you pay for until {nextBillingDate || 'the end of the current period'}.</li>
          <li>After that your account moves to Head Start (free): 1 school, up to 8 Subs, 12 postings per month.</li>
          <li>You can subscribe again at any time.</li>
        </ul>
      </ConfirmSubscriptionAction>
    </Card>
  );
}
